import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useFetch } from '../hooks/useFetch';
import { useOperators } from '../hooks/useOperators';
import { mapHosOperator, mapResOperator } from './OperatorContextHelper';
import { useAppView } from './AppViewContext';
import { BASE_URL, BASE_URL_MAP } from '../lib/environment';
import { useUser } from './UserContext';

const OperatorContext = createContext<OperatorContext | undefined>(undefined);

type UpdateField = keyof Operator | 'appKeys' | null;

export const OperatorProvider = (props: OperatorProviderProps) => {
  const [isChanging, setIsChanging] = useState(false);
  const [updateField, setUpdateField] = useState<UpdateField>(null);
  const operators = useOperators();
  const user = useUser();

  const [operator, setOperator] = useState<Operator>({
    id: 0,
    baseUrl: BASE_URL,
    code: '',
    name: '',
    models: [],
    systems: [],
    type: 'Hospitality',
  });
  const { loading, makeRequest, response } = useFetch();
  const appView = useAppView();

  const appViewOperator = useMemo(() => {
    const parsedOperatorId = Number(appView.id);
    if (!operators.data || operators.data.length === 0) return;

    const defaultOperator = operators.data.find(
      (o) => o.id === parsedOperatorId
    );

    return defaultOperator ?? operators.data[0];
  }, [operators.data, appView.id]);

  useEffect(() => {
    const operatorsLoaded = operators.data && operators.data.length > 0;

    if (!appViewOperator || appView.type !== 'Operator') return;
    if (!operatorsLoaded || !user.loggedIn) return;

    appView.changePendingView(
      {
        id: appViewOperator.id.toString(),
        code: appViewOperator.code,
        sector: appViewOperator.type,
        type: 'Operator',
      },
      true
    );

    let path = appView.baseUrl;

    if (appViewOperator.type === 'Hospitality') path += '/Operators';
    path += `/${appViewOperator.id}`;

    makeRequest(path, 'get');
  }, [operators.data, appViewOperator, user.loggedIn]);

  useEffect(() => {
    if (!loading && appViewOperator && !operators.isLoading && response) {
      if (!updateField) {
        if (appViewOperator.id !== response.ID) return;
        const summary = operators.data.find((o) => o.id === appViewOperator.id);

        if (summary?.type === 'Hospitality') {
          setOperator(mapHosOperator(response));
        }
        if (summary?.type === 'Residential') {
          setOperator(mapResOperator(response));
        }
        setIsChanging(false);
      } else handleActionResponse(response);
    }
  }, [loading, response, operators.data, operators.isLoading, appViewOperator]);

  const handleActionResponse = (response: any) => {
    if (operator.type === 'Residential') setOperator(mapResOperator(response));
    else setOperator(mapHosOperator(response));
  };

  const deleteFreeChannelGroup = () => {
    makeRequest(
      `${BASE_URL_MAP['Residential']}/${operator.id}/MediaFirstFreeChannelGroup`,
      'delete'
    );
  };

  const updateAppKeys = (appKeys: AppKeyUrls) => {
    setUpdateField('appKeys');
    const body: ApiResAppKeyUrls = {
      AppKeyURL1: appKeys.appKeyUrl1,
      AppKeyURL2: appKeys.appKeyUrl2,
      AppKeyURL3: appKeys.appKeyUrl3,
      AppKeyURL4: appKeys.appKeyUrl4,
    };
    const resEndpoint = `${BASE_URL_MAP['Residential']}/${operator.id}/AppKeys`;
    const hosEndpoint = `${BASE_URL_MAP['Hospitality']}/Operators/${operator.id}/AppKeyUrls`;
    const url = operator.type === 'Residential' ? resEndpoint : hosEndpoint;
    makeRequest(url, 'put', body);
  };

  const updateBranch = (branchId: string) => {
    setUpdateField('branch');
    const body = {
      ID: branchId,
    };
    makeRequest(
      `${BASE_URL_MAP['Residential']}/${operator.id}/UpdateBranch`,
      'put',
      body
    );
  };

  const updateDefaultCreditLimit = (amount: number) => {
    setUpdateField('defaultCreditLimit');
    makeRequest(
      `${BASE_URL_MAP['Residential']}/${operator.id}/CreditLimit`,
      'put',
      { Amount: amount }
    );
  };

  const updateFreeChannelGroup = (freeChannelGroupId: string | null) => {
    makeRequest(
      `${BASE_URL_MAP['Residential']}/${operator.id}/MediaFirstFreeChannelGroup/${freeChannelGroupId}`,
      'put'
    );
  };

  const updateHosTransports = (
    addTransports: number[],
    removeTransports: number[]
  ) => {
    setUpdateField('transports');
    const body = {
      AddTransportTypes: addTransports,
      RemoveTransportTypes: removeTransports,
    };
    makeRequest(
      `${BASE_URL_MAP['Hospitality']}/Operators/${operator.id}/TransportTypes`,
      'put',
      body
    );
  };

  const updateRecorderId = (recorderName: string) => {
    setUpdateField('mediaFirstRecorderId');
    makeRequest(
      `${BASE_URL_MAP['Residential']}/${operator.id}/MediaFirstRecorderId/${recorderName}`,
      'put'
    );
  };

  const updateShowSubscribedChannelsOnly = (enable: boolean) => {
    setUpdateField('showSubscribedChannelsOnly');
    const body = {
      Enable: enable,
    };
    makeRequest(
      `${BASE_URL_MAP['Residential']}/${operator.id}/ShowSubscribedChannelsOnly`,
      'put',
      body
    );
  };

  const updateSystems = (
    addSystemTypes: number[],
    removeSystemTypes: number[]
  ) => {
    setUpdateField('systems');
    makeRequest(
      `${BASE_URL_MAP['Hospitality']}/Operators/${operator.id}/SystemTypes`,
      'put',
      {
        AddSystemTypes: addSystemTypes,
        RemoveSystemTypes: removeSystemTypes,
      }
    );
  };

  const updateUseScreensaver = (enable: boolean) => {
    setUpdateField('usesScreensaver');
    const body = {
      Enable: enable,
    };
    makeRequest(
      `${BASE_URL_MAP['Residential']}/${operator.id}/UseScreensaver`,
      'put',
      body
    );
  };

  const updateOperatorState = (key: keyof Operator, state: any) => {
    setOperator(Object.assign({}, operator, { [key]: state }));
  };

  const updateOperator = (operator: Operator) => setOperator(operator);

  return (
    <OperatorContext.Provider
      value={{
        ...operator,
        deleteFreeChannelGroup,
        isChanging: isChanging,
        loading: loading,
        updateAppKeys,
        updateBranch,
        updateDefaultCreditLimit,
        updateFreeChannelGroup,
        updateHosTransports,
        updateRecorderId,
        updateShowSubscribedChannelsOnly,
        updateSystems,
        updateUseScreensaver,
        updateOperator,
        updateOperatorState,
        operators: operators.data,
      }}
    >
      {props.children}
    </OperatorContext.Provider>
  );
};

export const useOperator = () => {
  const context = useContext(OperatorContext);

  if (context === undefined)
    throw new Error('useOperator must be used within an OperatorProvider');

  return context;
};
